import { Box } from "@mui/material";
import {
  amber,
  blue,
  green,
  grey,
  orange,
  purple,
  red,
} from "@mui/material/colors";
import PropTypes from "prop-types";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";

/**
 * KpiWidget / KpiWidgetGroup
 *
 * The standard widget shape for KPI tiles across redesign tabs. Each
 * widget is a small bordered card with a tinted header (icon + uppercase
 * label + optional badge) and a body that shows the value plus an
 * optional subtitle. Visually mirrors the `ValidityGroup` cards in the
 * Documents tab — that styling is the reference design.
 *
 * Use `tone` to communicate state. Tones map to MUI color shades:
 *   - "neutral" (default) — plain gray
 *   - "blue", "green", "amber", "orange", "red", "purple"
 *
 * `KpiWidgetGroup` lays the widgets out in a responsive grid that wraps
 * to multi-row when it runs out of horizontal space.
 *
 * Items shape (each entry in the `items` array):
 *   {
 *     key,        // unique id (falls back to `label`)
 *     label,      // small uppercase label in the header
 *     value,      // primary value (string/number/node)
 *     subtitle,   // small line under the value
 *     icon,       // optional icon next to the label
 *     tone,       // one of the keys in TONE_PALETTES
 *     badge,      // optional small pill on the right side of the header
 *     valueColor, // override the body value color (rare — prefer `tone`)
 *   }
 */

const FONT_MONO = '"JetBrains Mono", "SF Mono", Menlo, monospace';

export const KPI_TONE_PALETTES = {
  neutral: {
    color: "#15151a",
    // color: grey[800],
    // headerText: "#15151a",
    headerText: grey[900],
    // headerText: '#64748b,
    bg: "#ffffff",
    border: "#d8e0ea",
    // border: grey[200],
  },
  blue: {
    color: blue[800],
    headerText: blue[800],
    bg: blue[50],
    border: blue[200],
  },
  green: {
    color: green[800],
    headerText: green[800],
    bg: green[50],
    border: green[200],
  },
  amber: {
    color: amber[900],
    headerText: amber[900],
    bg: amber[50],
    border: amber[200],
  },
  orange: {
    color: orange[800],
    headerText: orange[800],
    bg: orange[50],
    border: orange[200],
  },
  red: { color: red[800], headerText: red[800], bg: red[50], border: red[200] },
  purple: {
    color: purple[800],
    headerText: purple[800],
    bg: purple[50],
    border: purple[200],
  },
};

/**
 * Convenience helper: map a numeric-progress pair (e.g. "3 of 5") to the
 * canonical KPI tone. Returns undefined when the denominator is 0 so
 * empty datasets don't read as failures.
 */
export const progressTone = (numerator, total) => {
  if (!total) return undefined;
  if (numerator >= total) return "green";
  if (numerator <= 0) return "red";
  return "amber";
};

export const KpiWidget = ({
  label,
  value,
  subtitle,
  icon,
  tone = "neutral",
  badge,
  valueColor,
  suppressBoxShadow = false,
  onClick,
  sx,
}) => {
  const palette = KPI_TONE_PALETTES[tone] || KPI_TONE_PALETTES.neutral;
  return (
    <Box
      onClick={onClick}
      sx={{
        border: `1px solid ${palette.border}`,
        // border: '0.5px solid rgba(0, 0, 0, 0.08)',
        // borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "#fff",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 120ms ease",
        // -----
        // background: "#ffffff",
        // overflow: "hidden",
        // border: "1px solid #d8e0ea",
        borderRadius: "8px",
        // bgcolor: palette.bg,
        // borderRadius: "12px",
        boxShadow: suppressBoxShadow
          ? "none"
          : "0 1px 2px rgba(15, 23, 42, 0.04),0 2px 6px rgba(15, 23, 42, 0.05)",
        ...(onClick && {
          "&:hover": {
            boxShadow: "0 2px 6px rgba(20,20,30,0.06)",
          },
        }),
        ...sx,
      }}
    >
      {/* Header — tinted band with icon + label + optional badge.
          Mirrors the ValidityGroup header in the Documents tab so all
          KPI cards across the app read with the same affordance. */}
      <Flx
        ac
        g={0.75}
        sx={{
          // px: 1.5,
          // py: 0.9,
          padding: "16px 18px",
          pb: "0px",
          // pb: "12px",
          // bgcolor: palette.bg,
          // borderBottom: `1px solid ${palette.border}`,
        }}
      >
        {icon ? (
          <Box
            sx={{
              color: palette.headerText,
              display: "flex",
              flexShrink: 0,
              "& svg": { fontSize: 14 },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Txt
          component="div"
          sx={{
            // fontWeight: 700,
            // color: palette.headerText,
            // textTransform: "uppercase",
            // letterSpacing: "0.06em",
            // flex: 1,
            // minWidth: 0,
            // whiteSpace: "nowrap",
            // overflow: "hidden",
            // textOverflow: "ellipsis",
            // fontSize: "10.5px",
            // -------
            // lineHeight: 1.5,
            // fontWeight: 500,
            // fontSize: "10px",
            // color: "#64748b",
            // textTransform: "uppercase",
            // letterSpacing: "0.1em",
            // marginBottom: "6px",

            // --
            lineHeight: 1.5,
            fontWeight: 500,
            fontSize: 11,
            color: "#5f5e5a",
            // color: palette.bg,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Txt>
        {badge != null && badge !== false ? (
          <Txt
            component="span"
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              fontFamily: FONT_MONO,
              px: 0.75,
              py: "2px",
              borderRadius: "4px",
              bgcolor: "#fff",
              color: palette.color,
              border: `1px solid ${palette.border}`,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {badge}
          </Txt>
        ) : null}
      </Flx>
      {/* Body — value + optional subtitle. */}
      <Box
        sx={{
          // px: 1.5,
          // py: 1.25,
          //
          padding: "16px 18px",
          pt: 1,
          // paddingBottom: "4px",
        }}
      >
        <Txt
          component="div"
          sx={{
            fontFamily: FONT_MONO,
            // fontSize: "18px",
            fontWeight: 600,
            // lineHeight: 1.2,
            // color: valueColor || "#15151a",
            // whiteSpace: "nowrap",
            // overflow: "hidden",
            // textOverflow: "ellipsis",
            lineHeight: 1.5,
            // fontWeight: 500,
            fontSize: 20,
            // fontSize: 18,
            color: "#1a1a18",
            // marginTop: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value ?? "—"}
        </Txt>
        {subtitle ? (
          <Txt
            component="div"
            sx={{
              mt: "3px",
              fontSize: "11px",
              color: grey[600],
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {subtitle}
          </Txt>
        ) : null}
      </Box>
    </Box>
  );
};

KpiWidget.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  tone: PropTypes.oneOf(Object.keys(KPI_TONE_PALETTES)),
  badge: PropTypes.node,
  valueColor: PropTypes.string,
  onClick: PropTypes.func,
  sx: PropTypes.object,
};

const KpiWidgetGroup = ({ items = [], minWidth = 170, gap = 1.5, sx }) => {
  if (!items.length) return null;
  return (
    <Box
      sx={{
        display: "grid",
        // Responsive: each widget claims minWidth px minimum, but the
        // row fills available space evenly and wraps to a new line when
        // it runs out of horizontal room.
        gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
        gap,
        ...sx,
      }}
    >
      {items.map((item, idx) => (
        <KpiWidget key={item.key || item.label || idx} {...item} />
      ))}
    </Box>
  );
};

KpiWidgetGroup.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(KpiWidget.propTypes)),
  minWidth: PropTypes.number,
  gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
};

export default KpiWidgetGroup;
